import { Inject, Injectable } from "@angular/core";
import { MSAL_GUARD_CONFIG, MsalGuardConfiguration, MsalService } from "@azure/msal-angular";
import { AuthenticationResult, BrowserAuthError, PopupRequest } from "@azure/msal-browser";
import { environment } from "../../environments/environment";
import { HttpErrorsEnum } from "../models/shared.model";
import { SettingsService } from "./settings.service";

@Injectable()
export class OfficeLoginService {

  private _accessToken: string = "";
  private _idToken: string = "";
  private _tokenScopes: any;
  private _loginInProgress: boolean = false;

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private authService: MsalService,
    private settingService: SettingsService
  ) {
  }

  get tokenScopes(): any {
    return this._tokenScopes;
  }

  get accessToken(): string {
    return this._accessToken;
  }

  get idToken(): string {
    return this._idToken;
  }

  isPopupAllowed(): boolean {
    const testPopup = window.open("", "_blank", "width=1,height=1");
    if (!testPopup || testPopup.closed || typeof testPopup.closed === "undefined") {
      return false; // Popup is blocked
    }
    testPopup.close();
    return true; // Popup is allowed
  }

  private getLoginScopes(include_alter_inbox_scope: boolean = false) {
    let tokenScopes = environment.apiConfig.scopes;

    if (include_alter_inbox_scope) {
      tokenScopes = tokenScopes.concat(environment.apiConfig.alter_inbox_scope);
    }
    return tokenScopes;
  }

  private successLoginPopup(response: AuthenticationResult) {
    this.authService.instance.setActiveAccount(response.account);
    this._accessToken = response.accessToken;
    this._idToken = response.idToken;
    this._tokenScopes = response.scopes || [];
  }

  getAccountForLogin(email: string){
    const accounts = this.authService.instance.getAllAccounts();

    let account = accounts.find(a => a.username?.toLowerCase() === email);
    if (!account) {
      account = accounts.find(a => a.idTokenClaims?.preferred_username?.toLowerCase() === email);
    }
    return account;
  }

  async login(include_alter_inbox_scope: boolean = false): Promise<string> {
    if (this._loginInProgress) {
      console.warn("Login already in progress, waiting for completion");
      return this._accessToken;
    }

    this._loginInProgress = true;

    try {
      const tokenScopes = this.getLoginScopes(include_alter_inbox_scope);

      const handlePopupDisabled = (reject: Function) => reject({ error: HttpErrorsEnum.POPUP_DISABLED });
      const handleCantLogin = (reject: Function) => reject({ error: HttpErrorsEnum.CANT_LOGIN });

      return await new Promise((resolve, reject) => {
        this.authService.initialize().subscribe(() => {
          const mailboxEmailAddress = Office.context.mailbox.userProfile.emailAddress.toLowerCase();
          const account = this.getAccountForLogin(mailboxEmailAddress);

          const active = this.authService.instance.getActiveAccount();

          //In case of user have multiple accounts logged in to outlook, and the cached token is not for the selected mailbox
          if (active && (active.username?.toLowerCase() !== mailboxEmailAddress) &&
            (active.idTokenClaims?.preferred_username?.toLowerCase() !== mailboxEmailAddress)) {
            // Clear active to avoid accidental cross-account silent tokens
            this.authService.instance.setActiveAccount(null as any);
          }

          //User already signed in → Try silent login
          if (account) {
            this.authService.instance.acquireTokenSilent({ scopes: tokenScopes, account })
              .then(response => {
                this.successLoginPopup(response);
                resolve(this._accessToken);
              })
              .catch(error => {
                if (!this.isPopupAllowed()) return handlePopupDisabled(reject);

                this.authService.instance.acquireTokenPopup({ scopes: tokenScopes })
                  .then(response => {
                    this.successLoginPopup(response);
                    resolve(this._accessToken);
                  })
                  .catch(err => {
                    if (err instanceof BrowserAuthError && err.errorCode === "popup_window_error") {
                      return handlePopupDisabled(reject);
                    }
                    console.log(err);
                    handleCantLogin(reject);
                  });
              });
            return;
          }

          //First login (no account cached)
          if (!this.isPopupAllowed()) return handlePopupDisabled(reject);

          this.authService.instance.loginPopup(this.msalGuardConfig.authRequest as PopupRequest)
            .then(response => {
              this.successLoginPopup(response);
              resolve(this._accessToken);
            })
            .catch(err => {
              if (err instanceof BrowserAuthError && err.errorCode === "popup_window_error") {
                return handlePopupDisabled(reject);
              }
              console.log(err);
              handleCantLogin(reject);
            });
        });
      });
    } finally {
      this._loginInProgress = false;
    }
  }
}
